import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent } from '@/components/ui';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { PublicStripePaymentConfirm } from '@/components/public/PublicStripePaymentConfirm';
import { formatCurrency, type Currency } from '@/utils/currency';
import {
  publicClubMembershipService,
  type MemberPortalMembership,
} from '@/services/publicClubMembership';
import { MembershipPortalAside } from '@/components/membership/MembershipPortalAside';
import type { MembershipPurchaseResponse } from '@/services/clubMembers';
import { cn } from '@/lib/utils';

function formatCentsAsMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

import {
  MEMBERSHIP_STATUS_BADGE_CLASSES,
  MEMBERSHIP_STATUS_LABELS,
} from '@/components/membership/membershipPortalShared';

export interface MembershipSectionProps {
  clubSlug: string;
  clubName?: string;
  clubLogoUrl?: string | null;
  joinPath?: string;
  /** When true, omit outer page header (inline dashboard tab). */
  embedded?: boolean;
}

export function MembershipSection({
  clubSlug,
  clubName,
  clubLogoUrl,
  joinPath,
  embedded = false,
}: MembershipSectionProps) {
  const queryClient = useQueryClient();
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [renewPurchase, setRenewPurchase] = useState<MembershipPurchaseResponse | null>(null);
  const [pendingPurchase, setPendingPurchase] = useState<MembershipPurchaseResponse | null>(null);
  const [pendingCheckoutId, setPendingCheckoutId] = useState<string | null>(null);
  const [resumingCheckoutId, setResumingCheckoutId] = useState<string | null>(null);
  const [cancellingCheckoutId, setCancellingCheckoutId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);

  const resolvedJoinPath = joinPath ?? `/club/${clubSlug}/join`;

  const portalQuery = useQuery({
    queryKey: ['member-portal', clubSlug],
    enabled: Boolean(clubSlug),
    queryFn: async () => {
      const res = await publicClubMembershipService.getMyMembership(clubSlug);
      if (res.success && res.data) {
        return { portal: res.data, notMemberReason: null as string | null };
      }
      return {
        portal: null,
        notMemberReason: res.error || 'You are not a member of this club',
      };
    },
  });

  const portal = portalQuery.data?.portal ?? null;
  const notMemberReason = portalQuery.data?.notMemberReason ?? null;
  const loading = portalQuery.isLoading;

  if (!clubSlug) {
    return (
      <div className={`text-center text-muted-foreground ${embedded ? 'py-8' : 'p-6'}`}>
        Membership is not available for this organization.
      </div>
    );
  }

  const startRenew = async (membershipId: string) => {
    if (!clubSlug) return;
    setRenewingId(membershipId);
    try {
      const res = await publicClubMembershipService.renew(clubSlug, { membershipId });
      if (res.success && res.data) {
        setRenewPurchase(res.data);
      } else {
        toast.error(res.error || 'Failed to renew your membership');
        setRenewingId(null);
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to renew your membership');
      setRenewingId(null);
    }
  };

  const handleRenewSuccess = () => {
    toast.success('Membership renewed!');
    setRenewingId(null);
    setRenewPurchase(null);
    void queryClient.invalidateQueries({ queryKey: ['member-portal', clubSlug] });
  };

  const startPendingCheckout = async (membershipId: string) => {
    if (!clubSlug) return;
    setResumingCheckoutId(membershipId);
    setPendingCheckoutId(membershipId);
    try {
      const res = await publicClubMembershipService.resumeCheckout(clubSlug, membershipId);
      if (res.success && res.data?.clientSecret) {
        setPendingPurchase(res.data);
      } else {
        toast.error(res.error || 'Failed to resume payment');
        setPendingCheckoutId(null);
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to resume payment');
      setPendingCheckoutId(null);
    } finally {
      setResumingCheckoutId(null);
    }
  };

  const cancelPendingCheckout = async (membershipId: string) => {
    if (!clubSlug) return;
    setCancellingCheckoutId(membershipId);
    try {
      const res = await publicClubMembershipService.cancelCheckout(clubSlug, membershipId);
      if (res.success) {
        toast.success('Checkout cancelled');
        if (pendingCheckoutId === membershipId) {
          setPendingCheckoutId(null);
          setPendingPurchase(null);
        }
        void queryClient.invalidateQueries({ queryKey: ['member-portal', clubSlug] });
      } else {
        toast.error(res.error || 'Failed to cancel checkout');
      }
    } finally {
      setCancellingCheckoutId(null);
    }
  };

  const handlePendingCheckoutSuccess = () => {
    toast.success('Payment received! Your membership is being activated.');
    setPendingCheckoutId(null);
    setPendingPurchase(null);
    void queryClient.invalidateQueries({ queryKey: ['member-portal', clubSlug] });
  };

  const confirmCancelMembership = async () => {
    if (!cancelConfirmId) return;
    setCancellingId(cancelConfirmId);
    try {
      const res = await publicClubMembershipService.cancel(clubSlug, {
        membershipId: cancelConfirmId,
      });
      if (res.success) {
        toast.success('Membership cancelled');
        setCancelConfirmId(null);
        void queryClient.invalidateQueries({ queryKey: ['member-portal', clubSlug] });
      } else {
        toast.error(res.error || 'Failed to cancel membership');
      }
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[200px] p-6'}`}>
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" aria-label="Loading" />
      </div>
    );
  }

  if (notMemberReason || !portal) {
    return (
      <div className={embedded ? '' : 'mx-auto max-w-lg p-6 text-center'}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {embedded ? 'Club membership' : 'No membership found'}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{notMemberReason}</p>
        <Button className="mt-4" asChild>
          <Link to={resolvedJoinPath}>Join this club</Link>
        </Button>
      </div>
    );
  }

  const displayName = clubName ?? portal.club.name;
  const displayLogo = clubLogoUrl ?? portal.club.logoUrl;

  const sortedMemberships = portal.memberships.slice().sort((a, b) => {
    const rank = (m: MemberPortalMembership) =>
      m.status === 'active' ||
      m.status === 'pending_payment' ||
      m.status === 'pending_verification' ||
      m.status === 'past_due'
        ? 0
        : 1;
    return rank(a) - rank(b);
  });

  return (
    <div className={embedded ? '' : 'mx-auto max-w-2xl p-6'}>
      {!embedded ? (
        <header className="mb-6 flex items-center gap-3">
          {displayLogo ? (
            <img src={displayLogo} alt={displayName} className="h-10 w-10 rounded-full object-cover" />
          ) : null}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName} membership</h1>
          </div>
        </header>
      ) : null}

      {renewPurchase && renewingId ? (
        <Card className="mb-6 overflow-hidden border-border/80 bg-card shadow-sm">
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Confirm renewal payment</h2>
            {renewPurchase.clientSecret ? (
              <PublicStripePaymentConfirm
                clientSecret={renewPurchase.clientSecret}
                stripeConnectAccountId={renewPurchase.stripeConnectAccountId}
                amountCents={renewPurchase.totalCents}
                currency={renewPurchase.currency}
                onSuccess={handleRenewSuccess}
                onCancel={() => {
                  setRenewPurchase(null);
                  setRenewingId(null);
                }}
              />
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your renewal has been started. Check your email for next steps.
              </p>
            )}
          </CardContent>
        </Card>
      ) : null}

      {pendingPurchase && pendingCheckoutId ? (
        <Card className="mb-6 overflow-hidden border-border/80 bg-card shadow-sm">
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Complete membership payment</h2>
            <PublicStripePaymentConfirm
              clientSecret={pendingPurchase.clientSecret!}
              stripeConnectAccountId={pendingPurchase.stripeConnectAccountId}
              amountCents={pendingPurchase.totalCents}
              currency={pendingPurchase.currency}
              onSuccess={handlePendingCheckoutSuccess}
              onCancel={() => {
                setPendingPurchase(null);
                setPendingCheckoutId(null);
              }}
            />
          </CardContent>
        </Card>
      ) : null}

      {sortedMemberships.length === 0 ? (
        <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
          <CardContent className="p-8 text-center text-sm text-gray-600 dark:text-gray-400">
            You do not have any memberships at this club yet.
          </CardContent>
        </Card>
      ) : (
        <div
          className={cn(
            embedded
              ? 'grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,22rem)] lg:items-start'
              : 'space-y-4'
          )}
        >
          <div className="space-y-4">
            {sortedMemberships.map((membership) => {
            const canRenew =
              membership.termType === 'fixed_period' &&
              (membership.status === 'active' || membership.status === 'expired') &&
              !renewPurchase &&
              !pendingPurchase;
            const canCancel =
              (membership.status === 'active' ||
                membership.status === 'past_due' ||
                membership.status === 'pending_verification') &&
              !renewPurchase &&
              !pendingPurchase;
            const canResumeCheckout =
              membership.status === 'pending_payment' && !renewPurchase && !pendingPurchase;
            return (
              <Card key={membership.id} className="overflow-hidden border-border/80 bg-card shadow-sm">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {membership.membershipType?.name ?? 'Membership'}
                      </h3>
                      {membership.seasonLabel ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Season {membership.seasonLabel}</p>
                      ) : null}
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${MEMBERSHIP_STATUS_BADGE_CLASSES[membership.status]}`}
                    >
                      {MEMBERSHIP_STATUS_LABELS[membership.status]}
                    </span>
                  </div>

                  {membership.status === 'pending_verification' ? (
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      The club is verifying your eligibility for this plan. You&apos;ll be notified once it&apos;s
                      approved.
                    </p>
                  ) : null}

                  {membership.status === 'pending_payment' ? (
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Your membership is waiting for payment. Complete checkout to activate it, or cancel to start
                      again.
                    </p>
                  ) : null}

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Started</p>
                      <p className="font-medium text-gray-900 dark:text-white">{formatDate(String(membership.startDate))}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">
                        {membership.termType === 'recurring' ? 'Renews' : 'Expires'}
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {membership.endDate ? formatDate(String(membership.endDate)) : 'Ongoing'}
                      </p>
                    </div>
                  </div>

                  {membership.seats.length > 0 && !embedded ? (
                    <div>
                      <p className="mb-1 text-sm font-medium text-gray-900 dark:text-white">Covered people</p>
                      <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {membership.seats.map((seat) => (
                          <li key={seat.memberId}>
                            {seat.firstName} {seat.lastName}
                            {seat.isPrimary ? ' (primary)' : ''} — {seat.seatType}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {membership.addonSelections.length > 0 ? (
                    <div>
                      <p className="mb-1 text-sm font-medium text-gray-900 dark:text-white">Add-ons</p>
                      <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {membership.addonSelections.map((selection, index) => (
                          <li key={`${selection.addonId}-${index}`}>
                            {selection.name} — {formatCentsAsMoney(selection.totalCents, membership.currency)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
                    <span className="mr-auto text-sm font-semibold text-gray-900 dark:text-white">
                      Total {formatCentsAsMoney(membership.totalCents, membership.currency)}
                    </span>
                    {canResumeCheckout ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => startPendingCheckout(membership.id)}
                          isLoading={resumingCheckoutId === membership.id}
                          disabled={
                            (resumingCheckoutId != null && resumingCheckoutId !== membership.id) ||
                            cancellingCheckoutId != null
                          }
                        >
                          Complete payment
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => void cancelPendingCheckout(membership.id)}
                          isLoading={cancellingCheckoutId === membership.id}
                          disabled={
                            (cancellingCheckoutId != null && cancellingCheckoutId !== membership.id) ||
                            resumingCheckoutId != null
                          }
                        >
                          Cancel checkout
                        </Button>
                      </>
                    ) : null}
                    {canCancel ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setCancelConfirmId(membership.id)}
                        isLoading={cancellingId === membership.id}
                        disabled={cancellingId != null && cancellingId !== membership.id}
                      >
                        Cancel membership
                      </Button>
                    ) : null}
                    {canRenew ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startRenew(membership.id)}
                        isLoading={renewingId === membership.id}
                      >
                        <RefreshCw className="h-4 w-4" aria-hidden />
                        Renew
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </div>

          {embedded ? (
            <MembershipPortalAside
              clubSlug={clubSlug}
              memberships={sortedMemberships}
              className="mt-2 lg:mt-0"
            />
          ) : null}
        </div>
      )}

      {!embedded ? (
        <MembershipPortalAside clubSlug={clubSlug} memberships={sortedMemberships} className="mt-10" />
      ) : null}

      <AlertDialog
        open={cancelConfirmId != null}
        onOpenChange={(open) => !cancellingId && !open && setCancelConfirmId(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel membership?</AlertDialogTitle>
            <AlertDialogDescription>
              Your membership will end and you will be removed from upcoming play events at this club.
              Payments already collected are not refunded automatically — contact the club if you need help.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={cancellingId != null}>
              Keep membership
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={cancellingId != null}
              onClick={() => void confirmCancelMembership()}
            >
              Cancel membership
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
